import { measureOpenAISignup } from "@seamapi/shared/openai-ads"
import { useSessionState } from "core/hooks"
import { useQuery } from "@tanstack/react-query"
import { useSignOut } from "./use-sign-out"
import { getAuthenticatedAxios } from "./use-authenticated-axios"
import { dayjs } from "@seamapi/shared/dayjs"
import toast from "react-hot-toast"
import { useEffect } from "react"
import { isAxiosError } from "axios"
import { FullStory } from "@fullstory/browser"
import posthog from "posthog-js"
import { useClerkCreatedAt } from "core/hooks/use-clerk-created-at"
import { useHasSession } from "core/hooks/use-session-key"

export const useMe = () => {
  const hasSession = useHasSession()
  const signOut = useSignOut()
  const timeZoneName = useSessionState((s) => s.timeZoneConfig.timeZoneName)
  const clerkCreatedAt = useClerkCreatedAt().createdAt?.toISOString()
  useEffect(() => {
    dayjs.tz.setDefault(timeZoneName)
  }, [timeZoneName])

  const query = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const axios = getAuthenticatedAxios()
      try {
        const {
          data: { signed_in_user },
        } = await axios.get(`/internal/sessions/get`)
        if (process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID) {
          try {
            FullStory("setIdentity", {
              uid: signed_in_user.user_id,
              properties: {
                displayName: `${signed_in_user.first_name} ${signed_in_user.last_name}`,
                email: signed_in_user.email,
              },
            })
          } catch (fsErr) {
            console.warn("FullStory setIdentity failed (non-fatal)", fsErr)
          }
        }
        measureOpenAISignup(signed_in_user)

        // The internal session endpoint returns preferred_workspace_id ahead of the
        // generated session types; augment until @seamapi/types regenerates. Same
        // internal-field pattern as use-workspaces.ts.
        return signed_in_user as typeof signed_in_user & {
          preferred_workspace_id: string | null
        }
      } catch (e) {
        const seamHealthRes = await axios
          .get("/health")
          .catch(() => ({ data: { ok: false } }))

        if (!seamHealthRes.data.ok) {
          toast.error("Error getting session, Seam API is not responding", {
            id: "seam-api-down",
          })

          return
        }
        if (isAxiosError<{ error: { message: string } }>(e)) {
          console.error("Error getting session", e.response?.data)

          const isJWTMalformed =
            e.response?.data?.error?.message === "jwt malformed"

          if (isJWTMalformed) {
            toast.error(
              "Failed to get session! JWT is malformed. Logging you out..."
            )
            setTimeout(() => {
              signOut()
            }, 2000)
          }

          if (e.response?.status !== 401) {
            toast.error("Failed to get session!")
          }
        }
      }
    },
    enabled: hasSession,
  })

  // In a useEffect (not queryFn) so it re-fires when Clerk's createdAt loads after React Query returned cached data.
  const userId = query.data?.user_id
  const userEmail = query.data?.email
  useEffect(() => {
    if (userId == null) return
    posthog.identify(userId, {
      email: userEmail,
      ...(clerkCreatedAt ? { created_at: clerkCreatedAt } : {}),
    })
  }, [userId, userEmail, clerkCreatedAt])

  return query
}
